import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Inbox, Bug, Lightbulb, Loader2, ExternalLink, ImageIcon, ChevronDown, ChevronRight, RefreshCw, Paperclip, UserCheck, MessageSquarePlus, Clock, ArrowRight, Send, Mail, Trash2, Link2, AlertTriangle } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { readFileAsBase64, AttachmentList, AttachmentDisplay } from '@/components/discussion/attachments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRIORITY_COLOR = {
  low: 'bg-slate-500/15 text-slate-300 border-slate-500/20',
  medium: 'bg-sky-500/15 text-sky-400 border-sky-500/20',
  high: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
  urgent: 'bg-rose-500/15 text-rose-400 border-rose-500/20',
};

const PRIORITY_FILTERS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const STATUS_FILTERS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];
const STATUS_OPTIONS = ['open', 'in_progress', 'resolved', 'closed'];
const STATUS_COLOR = {
  open: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  in_progress: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  resolved: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  closed: 'bg-muted text-muted-foreground border-border/40',
};
const prettyStatus = (s) => (s || '').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());

const eventText = (ev) => {
  if (ev.type === 'status') return `changed status ${prettyStatus(ev.from) || '—'} → ${prettyStatus(ev.to)}`;
  if (ev.type === 'priority') return `changed priority ${ev.from || '—'} → ${ev.to}`;
  if (ev.type === 'assignee') return ev.to ? `assigned to ${ev.to}` : 'unassigned';
  if (ev.type === 'question') return `${ev.author_name} asked reporter: "${ev.text}"`;
  if (ev.type === 'comment') return ev.from_reporter ? `reporter replied: "${ev.text}"` : `${ev.author_name}: "${ev.text}"`;
  if (ev.type === 'jira_linked') return ev.text || 'linked to Jira';
  return '';
};

export default function FeedbackInboxPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const myId = user?.user_id || user?.id;
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState('__all__');
  const [expanded, setExpanded] = useState(null);
  const [commentDraft, setCommentDraft] = useState({});
  const [commentAtt, setCommentAtt] = useState({});
  const [posting, setPosting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [openingAtt, setOpeningAtt] = useState(null);
  const [syncingJira, setSyncingJira] = useState(null);

  const syncJira = async (id) => {
    setSyncingJira(id);
    try {
      const res = await api.syncFeedbackToJira(id);
      if (res && res.ok === false) {
        setItems(prev => prev.map(it => it.feedback_id === id ? { ...it, jira_sync_error: res.error } : it));
        toast.error(res.error || 'Jira sync failed');
        return;
      }
      setItems(prev => prev.map(it => it.feedback_id === id
        ? { ...it, jira_issue_key: res.issue_key, jira_issue_url: res.issue_url, jira_sync_error: null } : it));
      toast.success(res.created === false ? `Updated ${res.issue_key} in Jira` : `Created ${res.issue_key} in Jira`);
    } catch (e) {
      setItems(prev => prev.map(it => it.feedback_id === id ? { ...it, jira_sync_error: e.message } : it));
      toast.error(e.message || 'Jira sync failed');
    }
    finally { setSyncingJira(null); }
  };

  // Open a feedback attachment WITHOUT a raw new browser tab — a raw-file tab on
  // the app origin shows the server's default favicon (a white-label leak). We
  // fetch it authenticated (httpOnly cookie) and preview images in the in-app
  // lightbox; any other file type downloads directly.
  const openAttachment = useCallback(async (it) => {
    setOpeningAtt(it.feedback_id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/feedback/attachment/${it.feedback_id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Attachment unavailable');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const ctype = res.headers.get('content-type') || blob.type || '';
      if (ctype.startsWith('image/')) {
        setLightbox(url);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = it.attachment?.name || 'attachment';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
    } catch (e) {
      toast.error(e.message || 'Could not open attachment');
    } finally {
      setOpeningAtt(null);
    }
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox((url) => { if (url && url.startsWith('blob:')) URL.revokeObjectURL(url); return null; });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listFeedback(undefined, undefined);
      setItems(res.items || []);
    } catch (e) { toast.error(e.message || 'Could not load feedback'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Deep-link support: /admin/feedback?id=<feedback_id> (from the super-admin
  // notification email) opens and scrolls to that specific item.
  useEffect(() => {
    const id = searchParams.get('id');
    if (id && items.some(it => it.feedback_id === id)) {
      setExpanded(id);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="feedback-item-${id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [items, searchParams]);
  useEffect(() => {
    api.getUsers().then(r => setUsers(r.users || r.items || r || [])).catch(() => {});
  }, []);

  const mergeEvents = (id, newEvents) => setItems(prev => prev.map(it => it.feedback_id === id
    ? { ...it, events: [...(it.events || []), ...(newEvents || [])] } : it));

  const changeStatus = async (id, status) => {
    try {
      const res = await api.updateFeedback(id, status);
      setItems(prev => prev.map(it => it.feedback_id === id ? { ...it, status, events: [...(it.events || []), ...(res.events || [])] } : it));
      toast.success(`Marked ${prettyStatus(status)}`);
    } catch (e) { toast.error(e.message || 'Could not update'); }
  };

  const assign = async (id, assigneeId) => {
    const val = assigneeId === '__unassigned__' ? null : assigneeId;
    try {
      const res = await api.assignFeedback(id, val);
      setItems(prev => prev.map(it => it.feedback_id === id ? { ...it, assignee_id: val, assignee_email: res.assignee_email || null, assignee_name: res.assignee_name || null, events: [...(it.events || []), ...(res.events || [])] } : it));
      toast.success(val ? `Assigned — ${res.assignee_name || 'user'} notified` : 'Unassigned');
    } catch (e) { toast.error(e.message || 'Could not assign'); }
  };

  const onPickCommentFiles = async (id, fileList) => {
    const files = Array.from(fileList || []);
    const picked = [];
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} is larger than 5MB`); continue; }
      try { picked.push({ name: f.name, size: f.size, type: f.type, data_url: await readFileAsBase64(f) }); }
      catch { toast.error(`Could not read ${f.name}`); }
    }
    if (picked.length) setCommentAtt(d => ({ ...d, [id]: [...(d[id] || []), ...picked].slice(0, 5) }));
  };

  const addComment = async (id, notifyReporter = false) => {
    const text = (commentDraft[id] || '').trim();
    const atts = commentAtt[id] || [];
    if (!text && !atts.length) return;
    setPosting(id);
    try {
      const res = await api.addFeedbackComment(id, text, notifyReporter, atts);
      mergeEvents(id, [res.event]);
      setCommentDraft(d => ({ ...d, [id]: '' }));
      setCommentAtt(d => ({ ...d, [id]: [] }));
      if (notifyReporter) toast.success('Question emailed to the reporter');
    } catch (e) { toast.error(e.message || 'Could not add note'); }
    finally { setPosting(null); }
  };

  const assigneeOptions = [...users].sort((a, b) =>
    (a.name || a.email || '').localeCompare(b.name || b.email || '', undefined, { sensitivity: 'base' }));

  const displayItems = items.filter(it =>
    (filter.length === 0 || filter.includes(it.status)) &&
    (priorityFilter.length === 0 || priorityFilter.includes(it.priority)) &&
    (assigneeFilter === '__all__'
      || (assigneeFilter === '__unassigned__' ? !it.assignee_id : it.assignee_id === assigneeFilter))
  );
  const counts = displayItems.reduce((acc, it) => { acc[it.type] = (acc[it.type] || 0) + 1; return acc; }, {});

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.deleteFeedback(confirmDelete.feedback_id);
      setItems(prev => prev.filter(it => it.feedback_id !== confirmDelete.feedback_id));
      if (expanded === confirmDelete.feedback_id) setExpanded(null);
      toast.success('Feedback deleted');
      setConfirmDelete(null);
    } catch (e) { toast.error(e.message || 'Could not delete'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="feedback-inbox-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Inbox className="w-6 h-6 text-primary" /> Feedback Inbox</h1>
          <p className="text-sm text-muted-foreground mt-1">Bug reports and feature requests submitted by your users.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="w-48 h-9 rounded-lg text-sm font-normal" data-testid="feedback-assignee-filter">
              <UserCheck className="w-3.5 h-3.5 mr-1 shrink-0 text-muted-foreground" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" data-testid="feedback-assignee-filter-all">All Assignments</SelectItem>
              <SelectItem value="__unassigned__" data-testid="feedback-assignee-filter-unassigned">Unassigned</SelectItem>
              {assigneeOptions.map(u => (
                <SelectItem key={u.user_id} value={u.user_id} data-testid={`feedback-assignee-filter-${u.user_id}`}>{u.name || u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-44 justify-between rounded-lg text-sm font-normal" data-testid="feedback-priority-filter">
                <span className="truncate">
                  {priorityFilter.length === 0
                    ? 'All Priorities'
                    : priorityFilter.length === 1
                      ? (PRIORITY_FILTERS.find(p => p.value === priorityFilter[0])?.label || priorityFilter[0])
                      : `${priorityFilter.length} priorities`}
                </span>
                <ChevronDown className="w-4 h-4 opacity-60 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {PRIORITY_FILTERS.map(p => (
                <DropdownMenuCheckboxItem
                  key={p.value}
                  checked={priorityFilter.includes(p.value)}
                  onCheckedChange={(checked) => setPriorityFilter(prev => checked ? [...prev, p.value] : prev.filter(v => v !== p.value))}
                  onSelect={(e) => e.preventDefault()}
                  data-testid={`feedback-priority-option-${p.value}`}
                >
                  {p.label}
                </DropdownMenuCheckboxItem>
              ))}
              {priorityFilter.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setPriorityFilter([])} className="text-xs text-muted-foreground" data-testid="feedback-priority-clear">
                    Clear (show all)
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-44 justify-between rounded-lg text-sm font-normal" data-testid="feedback-status-filter">
                <span className="truncate">
                  {filter.length === 0
                    ? 'All Statuses'
                    : filter.length === 1
                      ? (STATUS_FILTERS.find(s => s.value === filter[0])?.label || filter[0])
                      : `${filter.length} statuses`}
                </span>
                <ChevronDown className="w-4 h-4 opacity-60 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {STATUS_FILTERS.map(s => (
                <DropdownMenuCheckboxItem
                  key={s.value}
                  checked={filter.includes(s.value)}
                  onCheckedChange={(checked) => setFilter(prev => checked ? [...prev, s.value] : prev.filter(v => v !== s.value))}
                  onSelect={(e) => e.preventDefault()}
                  data-testid={`feedback-status-option-${s.value}`}
                >
                  {s.label}
                </DropdownMenuCheckboxItem>
              ))}
              {filter.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setFilter([])} className="text-xs text-muted-foreground" data-testid="feedback-status-clear">
                    Clear (show all)
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="icon" className="rounded-lg" onClick={load} data-testid="feedback-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="flex gap-3 mb-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Bug className="w-3.5 h-3.5 text-rose-400" /> {counts.bug || 0} bugs</span>
        <span className="flex items-center gap-1"><Lightbulb className="w-3.5 h-3.5 text-amber-400" /> {counts.feature || 0} requests</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : displayItems.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="feedback-empty">
          <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
          {(filter.length > 0 || priorityFilter.length > 0 || assigneeFilter !== '__all__')
            ? <>No feedback matches the current filter. Try clearing it.</>
            : <>No feedback yet. Submissions from the in-app "+" widget will appear here.</>}
        </Card>
      ) : (
        <div className="space-y-3">
          {displayItems.map(it => {
            const isOpen = expanded === it.feedback_id;
            const TypeIcon = it.type === 'bug' ? Bug : Lightbulb;
            return (
              <Card key={it.feedback_id} className="p-4" data-testid={`feedback-item-${it.feedback_id}`}>
                <div className="flex items-start gap-3">
                  <TypeIcon className={`w-5 h-5 mt-0.5 shrink-0 ${it.type === 'bug' ? 'text-rose-400' : 'text-amber-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{it.title || (it.type === 'bug' ? 'Bug report' : 'Feature request')}</span>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_COLOR[it.status] || ''}`}>{prettyStatus(it.status)}</Badge>
                      {it.priority && <Badge variant="outline" className={`text-[10px] capitalize ${PRIORITY_COLOR[it.priority] || ''}`} data-testid={`feedback-priority-badge-${it.feedback_id}`}>{it.priority}</Badge>}
                      {it.screenshot && <Badge variant="secondary" className="text-[10px] gap-1"><ImageIcon className="w-3 h-3" /> Screenshot</Badge>}
                      {it.attachment && <Badge variant="secondary" className="text-[10px] gap-1"><Paperclip className="w-3 h-3" /> File</Badge>}
                      {it.jira_issue_key && (
                        <a href={it.jira_issue_url || '#'} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`feedback-jira-link-${it.feedback_id}`}>
                          <Badge variant="outline" className="text-[10px] gap-1 border-blue-500/30 text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 cursor-pointer"><Link2 className="w-3 h-3" /> {it.jira_issue_key}</Badge>
                        </a>
                      )}
                      {!it.jira_issue_key && it.jira_sync_error && (
                        <Badge variant="outline" className="text-[10px] gap-1 border-rose-500/30 text-rose-400 bg-rose-500/10" title={it.jira_sync_error} data-testid={`feedback-jira-error-${it.feedback_id}`}><AlertTriangle className="w-3 h-3" /> Jira sync failed</Badge>
                      )}
                    </div>
                    <p className={`text-sm text-muted-foreground mt-1 ${isOpen ? '' : 'line-clamp-2'}`}>{it.description}</p>
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground/70 mt-2 flex-wrap">
                      <span>{it.user_email || 'unknown'}</span>
                      {it.page_url && <span className="font-mono">{it.page_url}</span>}
                      <span>{it.created_at ? new Date(it.created_at).toLocaleString() : ''}</span>
                      {it.assignee_email && <span className="flex items-center gap-1 text-primary/80"><UserCheck className="w-3 h-3" /> {it.assignee_name || it.assignee_email}</span>}
                    </div>

                    {isOpen && it.attachment && (
                      <button type="button" onClick={() => openAttachment(it)} disabled={openingAtt === it.feedback_id} className="inline-flex items-center gap-1.5 mt-3 text-xs text-primary hover:underline disabled:opacity-60" data-testid={`feedback-attachment-link-${it.feedback_id}`}>
                        {openingAtt === it.feedback_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} {it.attachment.name || 'Open attachment'}
                      </button>
                    )}

                    {isOpen && it.screenshot && (
                      <button type="button" onClick={() => setLightbox(it.screenshot)} className="block mt-3 rounded-lg overflow-hidden border border-border/40 max-w-xl cursor-zoom-in" data-testid={`feedback-screenshot-btn-${it.feedback_id}`}>
                        <img src={it.screenshot} alt="feedback screenshot" className="w-full" data-testid={`feedback-screenshot-${it.feedback_id}`} />
                      </button>
                    )}

                    {isOpen && (
                      <div className="mt-4 border-t border-border/30 pt-3" data-testid={`feedback-timeline-${it.feedback_id}`}>
                        <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5 mb-2"><Clock className="w-3.5 h-3.5" /> Notes & Timeline</div>
                        <div className="space-y-2 mb-3">
                          {(it.events || []).length === 0 && <p className="text-xs text-muted-foreground/80">No activity yet. Add an internal note below.</p>}
                          {(it.events || []).map(ev => (
                            <div key={ev.event_id} className="flex items-start gap-2 text-xs" data-testid={`feedback-event-${ev.event_id}`}>
                              {ev.type === 'comment'
                                ? <MessageSquarePlus className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                                : <ArrowRight className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />}
                              <div className="min-w-0">
                                <span className="text-foreground/90 font-medium">{ev.author_name}</span>{' '}
                                {ev.type === 'comment'
                                  ? <span className="text-foreground/80 whitespace-pre-wrap break-words">{ev.text}</span>
                                  : <span className="text-muted-foreground">{eventText(ev)}</span>}
                                <span className="text-muted-foreground/70 ml-1.5">· {ev.created_at ? new Date(ev.created_at).toLocaleString() : ''}</span>
                                <AttachmentDisplay attachments={ev.attachments} />
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            value={commentDraft[it.feedback_id] || ''}
                            onChange={e => setCommentDraft(d => ({ ...d, [it.feedback_id]: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter' && posting !== it.feedback_id) { e.preventDefault(); addComment(it.feedback_id); } }}
                            placeholder="Add an internal note…"
                            className="h-8 text-xs"
                            data-testid={`feedback-comment-input-${it.feedback_id}`}
                          />
                          <label className="h-8 px-2 inline-flex items-center rounded-lg border border-border/40 cursor-pointer hover:bg-muted/40 shrink-0" title="Attach files (max 5MB each)" data-testid={`feedback-attach-${it.feedback_id}`}>
                            <Paperclip className="w-3.5 h-3.5 text-muted-foreground" />
                            <input type="file" multiple className="hidden" onChange={e => { onPickCommentFiles(it.feedback_id, e.target.files); e.target.value = ''; }} />
                          </label>
                          <Button size="sm" className="h-8 text-xs gap-1.5 shrink-0" disabled={posting === it.feedback_id || (!(commentDraft[it.feedback_id] || '').trim() && !(commentAtt[it.feedback_id] || []).length)} onClick={() => addComment(it.feedback_id)} data-testid={`feedback-comment-submit-${it.feedback_id}`}>
                            {posting === it.feedback_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Note
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 shrink-0" disabled={posting === it.feedback_id || (!(commentDraft[it.feedback_id] || '').trim() && !(commentAtt[it.feedback_id] || []).length) || !it.user_email} title={it.user_email ? 'Email this question to the reporter' : 'Reporter has no email on file'} onClick={() => addComment(it.feedback_id, true)} data-testid={`feedback-ask-reporter-${it.feedback_id}`}>
                            <Mail className="w-3.5 h-3.5" /> Ask reporter
                          </Button>
                        </div>
                        <AttachmentList attachments={commentAtt[it.feedback_id]} onRemove={(i) => setCommentAtt(d => ({ ...d, [it.feedback_id]: (d[it.feedback_id] || []).filter((_, idx) => idx !== i) }))} />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Select value={it.status} onValueChange={v => changeStatus(it.feedback_id, v)}>
                      <SelectTrigger className="w-32 h-8 rounded-lg text-xs" data-testid={`feedback-status-${it.feedback_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{prettyStatus(s)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={it.assignee_id || '__unassigned__'} onValueChange={v => assign(it.feedback_id, v)}>
                      <SelectTrigger className="w-40 h-8 rounded-lg text-xs" data-testid={`feedback-assignee-${it.feedback_id}`}>
                        <UserCheck className="w-3.5 h-3.5 mr-1 shrink-0 text-muted-foreground" /><SelectValue placeholder="Assign to…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unassigned__">Unassigned</SelectItem>
                        {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {(
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => setExpanded(isOpen ? null : it.feedback_id)} data-testid={`feedback-expand-${it.feedback_id}`}>
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5 mr-1" /> : <ChevronRight className="w-3.5 h-3.5 mr-1" />}{isOpen ? 'Less' : 'Details'}
                      </Button>
                    )}
                    {it.page_url && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => window.open(it.page_url, '_blank')} data-testid={`feedback-goto-${it.feedback_id}`}>
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open page
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" disabled={syncingJira === it.feedback_id} onClick={() => syncJira(it.feedback_id)} title={it.jira_issue_key ? 'Push latest changes to the linked Jira issue' : 'Create a Jira issue for this feedback'} data-testid={`feedback-jira-sync-${it.feedback_id}`}>
                      {syncingJira === it.feedback_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Link2 className="w-3.5 h-3.5 mr-1" />} {it.jira_issue_key ? 'Sync Jira' : 'To Jira'}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-rose-400 hover:text-rose-300 hover:bg-rose-500/10" onClick={() => setConfirmDelete(it)} data-testid={`feedback-delete-${it.feedback_id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="feedback-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this feedback?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.title || confirmDelete?.description?.slice(0, 80) || 'This record'}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="feedback-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doDelete(); }} disabled={deleting} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="feedback-delete-confirm">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Trash2 className="w-4 h-4 mr-1.5" />} Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6 cursor-zoom-out" onClick={closeLightbox} data-testid="feedback-screenshot-lightbox">
          <img src={lightbox} alt="feedback screenshot full view" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <button type="button" onClick={closeLightbox} className="absolute top-4 right-4 text-white/80 hover:text-white text-sm bg-white/10 rounded-full px-3 py-1.5" data-testid="feedback-screenshot-lightbox-close">Close ✕</button>
        </div>
      )}
    </div>
  );
}
